import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const navLinks = [
    { name: 'Forside', path: '/' },
    { name: 'Diamantboring', path: '/diamantboring' },
    { name: 'Om', path: '/om-os' },
    { name: 'Galleri', path: '/galleri' },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-neutral-950/95 backdrop-blur-md border-b border-neutral-800/60 shadow-lg shadow-black/20 ${
          isScrolled
            ? 'py-3'
            : 'py-4 lg:py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group shrink-0">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=270,quality=90,format=auto/assets/0b4cb68f-6aab-477c-8129-a4a720b56096/logo.jpg"
              alt="Danry Diamant Boring ApS Logo"
              className="h-[60px] sm:h-[72px] w-auto object-contain rounded transition-transform duration-300 group-hover:scale-102"
            />
          </Link>

          {/* Desktop Navigation */}
          {!isMobile && (
            <nav className="flex items-center gap-5 xl:gap-8 mx-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative py-2 text-sm xl:text-base font-bold tracking-wider uppercase transition-colors duration-200 ${
                    isActive(link.path)
                      ? 'text-white font-extrabold border-b-2 border-brand-orange'
                      : 'text-neutral-400 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          )}

          {/* Desktop CTA */}
          {!isMobile && (
            <div className="flex items-center gap-3 xl:gap-4 shrink-0">
              <a
                href="tel:+4522971411"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-neutral-200 hover:border-brand-orange text-neutral-900 hover:text-brand-orange font-bold text-sm xl:text-base transition-all duration-200 shadow-sm"
              >
                <i className="fa-solid fa-phone text-brand-orange" />
                <span className="whitespace-nowrap">22 97 14 11</span>
              </a>
              <Link
                to="/kontakt"
                className="px-5 py-2.5 rounded-full bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm xl:text-base transition-all duration-200 shadow-md shadow-brand-orange/10"
              >
                Få et tilbud
              </Link>
            </div>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <div className="flex items-center gap-4">
              {/* Phone Icon on mobile header */}
              <a
                href="tel:+4522971411"
                className="flex items-center justify-center text-brand-orange hover:text-brand-orange-hover transition-colors text-xl p-2"
                aria-label="Ring til Danry"
              >
                <i className="fa-solid fa-phone" />
              </a>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center justify-center text-white hover:text-brand-orange transition-colors text-xl p-2"
                aria-label="Toggle menu"
              >
                <i className={`fa-solid ${isMenuOpen ? 'fa-xmark' : 'fa-bars'}`} />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Navigation Sidebar */}
      {isMobile && (
        <>
          {/* Backdrop Overlay */}
          <div
            className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity duration-300 ${
              isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Sidebar Panel */}
          <div
            className={`fixed top-0 right-0 bottom-0 w-[320px] max-w-[85vw] bg-neutral-950 border-l border-neutral-800/60 z-55 shadow-2xl flex flex-col h-full transition-transform duration-300 ease-in-out ${
              isMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {/* Sidebar Header */}
            <div className="flex items-center justify-between p-6 border-b border-neutral-900">
              <Link to="/" onClick={() => setIsMenuOpen(false)} className="flex items-center">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=140,quality=90,format=auto/assets/0b4cb68f-6aab-477c-8129-a4a720b56096/logo.jpg"
                  alt="Danry Diamant Boring ApS Logo"
                  className="h-12 w-auto object-contain rounded"
                />
              </Link>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="text-neutral-400 hover:text-white p-2 transition-colors"
                aria-label="Luk menu"
              >
                <i className="fa-solid fa-xmark text-2xl" />
              </button>
            </div>

            {/* Sidebar Content */}
            <div className="flex-1 overflow-y-auto p-6 flex flex-col justify-between">
              <nav className="flex flex-col gap-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`py-3.5 px-4 rounded-lg text-base font-extrabold uppercase tracking-wider transition-all flex items-center justify-between ${
                      isActive(link.path)
                        ? 'border-l-4 border-brand-orange text-brand-orange bg-neutral-900 pl-3 rounded-l-none'
                        : 'text-neutral-300 hover:bg-neutral-900 hover:text-white'
                    }`}
                  >
                    <span>{link.name}</span>
                    <i className={`fa-solid fa-chevron-right text-xs transition-transform ${isActive(link.path) ? 'text-brand-orange' : 'text-neutral-600'}`} />
                  </Link>
                ))}
              </nav>

              {/* Sidebar Footer */}
              <div className="mt-8 border-t border-neutral-900 pt-6 flex flex-col gap-4">
                <span className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                  Direkte kontakt
                </span>
                <a
                  href="tel:+4522971411"
                  className="flex items-center gap-3 text-lg font-extrabold text-white hover:text-brand-orange transition-colors"
                >
                  <i className="fa-solid fa-phone text-brand-orange" />
                  <span className="whitespace-nowrap">22 97 14 11</span>
                </a>
                <a
                  href="mailto:danrydiamant@gmail.com"
                  className="flex items-center gap-3 text-neutral-300 hover:text-brand-orange transition-colors text-sm"
                >
                  <i className="fa-solid fa-envelope text-brand-orange" />
                  <span className="whitespace-nowrap">danrydiamant@gmail.com</span>
                </a>
                <Link
                  to="/kontakt"
                  onClick={() => setIsMenuOpen(false)}
                  className="mt-4 w-full py-3.5 rounded-full bg-brand-orange hover:bg-brand-orange-hover text-white font-extrabold text-center transition-colors shadow-md shadow-brand-orange/10"
                >
                  Få et uforpligtende tilbud
                </Link>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
